import type { DevToolsEmptyStateProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsEmptyState/index.types';
import {
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import React from 'react';
import { cn } from '~/lib/utils';

/**
 * DevToolsEmptyState - What a tab says when it has nothing to show
 *
 * Deliberately generous rather than a one-line shrug.
 */
export const DevToolsEmptyState: React.FC<DevToolsEmptyStateProps> = ({
  title,
  body,
  icon,
  tone = 'muted',
  chips,
  hint,
  action,
  onAction,
  className,
}) => {
  const style = TONE[tone];

  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-xl border border-zinc-800 bg-zinc-900 px-[26px] pb-[30px] pt-[38px] text-center',
        className
      )}
    >
      {icon && (
        <span
          className={cn(
            'mb-3.5 flex h-[46px] w-[46px] items-center justify-center rounded-[13px] border',
            style.inset,
            style.text
          )}
        >
          {icon}
        </span>
      )}

      <p className="text-[14.5px] font-semibold tracking-[-0.01em] text-zinc-200">
        {title}
      </p>

      {body && (
        <p className="mt-1.5 max-w-[330px] text-xs leading-relaxed text-zinc-300">
          {body}
        </p>
      )}

      {chips && chips.length > 0 && (
        <div className="mt-3.5 flex flex-wrap justify-center gap-1.5">
          {chips.map((chip) => (
            <span
              key={chip}
              className="rounded-[20px] border border-zinc-800 bg-zinc-900 px-2 py-[3px] font-sp-mono text-[10.5px] text-zinc-400"
            >
              {chip}
            </span>
          ))}
        </div>
      )}

      {action && onAction && (
        <button
          type="button"
          onClick={onAction}
          className={cn(
            'mt-4 rounded-lg px-3.5 py-[7px] text-xs font-semibold transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40',
            tone === 'warn'
              ? 'bg-amber-400 text-zinc-950 hover:brightness-110'
              : 'border border-emerald-800 text-emerald-400 hover:bg-emerald-950'
          )}
        >
          {action}
        </button>
      )}

      {hint && (
        <div className="mt-[18px] flex w-full items-center justify-center gap-[7px] border-t border-zinc-800 pt-3.5">
          <span
            className={cn('h-[5px] w-[5px] flex-shrink-0 rounded-full', style.dot)}
          />
          <span className="font-sp-mono text-[10.5px] text-zinc-400">{hint}</span>
        </div>
      )}
    </div>
  );
};
